import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OverviewCard from '../components/OverviewCard';
import ScoreBadge from '../components/ScoreBadge';
import RoomSection from '../components/RoomSection';
import { AuthContext } from '../context/AuthContext';

import AddSite from '../components/AddSite';
import AddRoom from '../components/AddRoom';

import { faLeaf, faArrowLeft, faGrip, faList } from '@fortawesome/free-solid-svg-icons';
import { updateDevice, updateSite, deleteSite, updateRoom, deleteRoom } from '../utils/api';
import MoveDeviceModal from '../components/MoveDeviceModal';
import RenameDeviceModal from '../components/RenameDeviceModal';
import RenameModal from '../components/RenameModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { useCustomerData } from '../hooks/useCustomerData';
import { useModalState } from '../hooks/useModalState';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

const DashboardPage = () => {
        const { user } = useContext(AuthContext);
        const { customerId: urlCustomerId } = useParams();

        // Use URL customerId if admin is viewing another customer, otherwise use user's customerId
        const targetCustomerId = urlCustomerId || user?.customerId;
        const isViewingOtherCustomer = urlCustomerId && urlCustomerId !== String(user?.customerId);

        const { customer, loading, error, refresh, setCustomer } = useCustomerData(targetCustomerId);
        const [selectedSite, setSelectedSite] = useState(null);
        const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'thriving', or 'attention'

        // Sites are only worth showing to customers that have more than one. With a
        // single site the whole site level is hidden and that site is implicitly
        // active; selectedSite then only drives the multi-site tab bar.
        const isMultiSite = (customer?.sites?.length || 0) > 1;
        const activeSite = isMultiSite ? selectedSite : (customer?.sites?.[0] || null);
        const [viewMode, setViewMode] = useState(() => localStorage.getItem('dashboardViewMode') || 'cards');

        const handleViewModeChange = useCallback((mode) => {
                setViewMode(mode);
                localStorage.setItem('dashboardViewMode', mode);
        }, []);

        // Modal state using custom hook
        const moveModal = useModalState();
        const renameModal = useModalState();
        const siteRenameModal = useModalState();
        const siteDeleteModal = useModalState();
        const roomRenameModal = useModalState();
        const roomDeleteModal = useModalState();
        const { showToast } = useToast();

        // Memoized calculations
        const { allRooms, deviceCount, thrivingCount, attentionCount } = useMemo(() => {
                if (!customer?.sites) {
                        return { allRooms: [], deviceCount: 0, thrivingCount: 0, attentionCount: 0 };
                }

                const rooms = customer.sites.flatMap(site => site.rooms || []);
                const devices = rooms.flatMap(room => room.devices || []);
                const thriving = devices.filter(device => device.is_healthy);
                const needAttention = devices.filter(device => !device.is_healthy);

                return {
                        allRooms: rooms,
                        deviceCount: devices.length,
                        thrivingCount: thriving.length,
                        attentionCount: needAttention.length
                };
        }, [customer]);

        const roomsToShow = useMemo(() => {
                let rooms;

                if (activeSite) {
                        rooms = customer?.sites.find(site => site.id === activeSite.id)?.rooms || [];
                } else {
                        // Always get fresh rooms from customer.sites for "All locations"
                        rooms = customer?.sites.flatMap(site => site.rooms || []) || [];
                }

                // Filter devices based on active filter
                if (activeFilter === 'thriving') {
                        rooms = rooms.map(room => ({
                                ...room,
                                devices: room.devices?.filter(device => device.is_healthy) || []
                        })).filter(room => room.devices.length > 0);
                } else if (activeFilter === 'attention') {
                        rooms = rooms.map(room => ({
                                ...room,
                                devices: room.devices?.filter(device => !device.is_healthy) || []
                        })).filter(room => room.devices.length > 0);
                }

                // Sort devices by name in each room
                rooms = rooms.map(room => ({
                        ...room,
                        devices: [...(room.devices || [])].sort((a, b) => a.name.localeCompare(b.name))
                }));

                return rooms;
        }, [activeSite, customer, activeFilter]);

        // Handlers
        const handleRenameDevice = useCallback(async (deviceId, newName) => {
                try {
                        await updateDevice(deviceId, { name: newName });
                        renameModal.close();
                        refresh();
                        showToast('success', 'Device renamed successfully');
                } catch (err) {
                        console.error("Failed to rename device:", err);
                        showToast('error', 'Failed to rename device');
                }
        }, [renameModal, refresh, showToast]);

        const handleMoveDevice = useCallback(async (deviceId, roomId) => {
                try {
                        await updateDevice(deviceId, { room_id: parseInt(roomId) });
                        moveModal.close();
                        refresh();
                        showToast('success', 'Device moved successfully');
                } catch (err) {
                        console.error("Failed to move device:", err);
                        showToast('error', 'Failed to move device');
                }
        }, [moveModal, refresh, showToast]);

        const handleSiteChange = useCallback((site) => {
                setSelectedSite(site);
        }, []);

        const handleFilterChange = useCallback((filter) => {
                setActiveFilter(filter);
        }, []);

        const handleSiteAdded = useCallback((newSite) => {
                setCustomer(prevCustomer => ({
                        ...prevCustomer,
                        sites: [...prevCustomer.sites, newSite]
                }));
        }, [setCustomer]);

        const handleRoomAdded = useCallback((newRoom) => {
                setCustomer(prevCustomer => {
                        const newSites = prevCustomer.sites.map(site => {
                                if (site.id === newRoom.site_id) {
                                        return {
                                                ...site,
                                                rooms: [...(site.rooms || []), newRoom]
                                        };
                                }
                                return site;
                        });
                        return {
                                ...prevCustomer,
                                sites: newSites
                        };
                });
        }, [setCustomer]);

        // Site handlers
        const handleRenameSite = useCallback(async (siteId, newName) => {
                try {
                        const response = await updateSite(siteId, { name: newName });
                        siteRenameModal.close();
                        refresh();
                        if (!response.ok) {
                                throw new Error('Failed to rename site');
                        }
                        showToast('success', 'Site renamed successfully');
                } catch (err) {
                        console.error("Failed to rename site:", err);
                        showToast('error', 'Failed to rename site');
                }
        }, [siteRenameModal, refresh, showToast]);

        const handleDeleteSite = useCallback(async () => {
                const site = siteDeleteModal.item;
                try {
                        const response = await deleteSite(site.id);
                        if (response.status === 409) {
                                const data = await response.json();
                                showToast('error', data.message || 'Cannot delete site with rooms');
                                siteDeleteModal.close();
                                return;
                        }
                        if (!response.ok) {
                                throw new Error('Failed to delete site');
                        }
                        siteDeleteModal.close();
                        if (selectedSite?.id === site.id) {
                                setSelectedSite(null);
                        }
                        refresh();
                        showToast('success', 'Site deleted successfully');
                } catch (err) {
                        console.error("Failed to delete site:", err);
                        showToast('error', 'Failed to delete site');
                }
        }, [siteDeleteModal, selectedSite, refresh, showToast]);

        // Room handlers
        const handleRenameRoom = useCallback(async (roomId, newName) => {
                try {
                        await updateRoom(roomId, { name: newName });
                        roomRenameModal.close();
                        refresh();
                        showToast('success', 'Room renamed successfully');
                } catch (err) {
                        console.error("Failed to rename room:", err);
                        showToast('error', 'Failed to rename room');
                }
        }, [roomRenameModal, refresh, showToast]);

        const handleDeleteRoom = useCallback(async () => {
                const room = roomDeleteModal.item;
                try {
                        const response = await deleteRoom(room.id);
                        if (response.status === 409) {
                                const data = await response.json();
                                showToast('error', data.message || 'Cannot delete room with devices');
                                roomDeleteModal.close();
                                return;
                        }
                        if (!response.ok) {
                                throw new Error('Failed to delete room');
                        }
                        roomDeleteModal.close();
                        refresh();
                        showToast('success', 'Room deleted successfully');
                } catch (err) {
                        console.error("Failed to delete room:", err);
                        showToast('error', 'Failed to delete room');
                }
        }, [roomDeleteModal, refresh, showToast]);

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main id="main-dashboard" className="max-w-7xl mx-auto px-4 sm:px-8 py-8">

                                {isViewingOtherCustomer && (
                                        <div className="mb-6 flex items-center justify-between">
                                                <h1 data-testid="dashboard-customer-name" className="text-2xl font-bold text-text-strong">{customer?.name}</h1>
                                                <Link to="/org" data-testid="dashboard-back-to-org" className="inline-flex items-center text-brand hover:text-brand/80 transition-colors">
                                                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                                        Back to Organizations
                                                </Link>
                                        </div>
                                )}

                                {customer && customer.sites && customer.sites.length > 0 ? (
                                        <>
                                                {/* Dashboard Overview Section */}
                                                <section id="dashboard-overview" className="mb-12">
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                                                                <OverviewCard
                                                                        icon={faLeaf}
                                                                        iconBgClass="bg-brand/10"
                                                                        iconClass="text-brand"
                                                                        value={deviceCount}
                                                                        title="All Plants"
                                                                        testId="overview-all"
                                                                        description={isMultiSite ? 'Across all sites' : 'Across all rooms'}
                                                                        valueClass="text-text-strong"
                                                                        onClick={() => handleFilterChange('all')}
                                                                        isActive={activeFilter === 'all'}
                                                                />

                                                                <OverviewCard
                                                                        image="/images/leaf_good.png"
                                                                        iconBgClass="bg-status-ok/10"
                                                                        value={thrivingCount}
                                                                        title="Thriving Plants"
                                                                        testId="overview-thriving"
                                                                        description="No health issues"
                                                                        valueClass="text-status-ok"
                                                                        onClick={() => handleFilterChange('thriving')}
                                                                        isActive={activeFilter === 'thriving'}
                                                                />

                                                                <OverviewCard
                                                                        image="/images/leaf_bad.png"
                                                                        iconBgClass="bg-status-critical/10"
                                                                        value={attentionCount}
                                                                        title="Need Attention"
                                                                        testId="overview-attention"
                                                                        description="These plants needs some attention"
                                                                        valueClass="text-status-critical"
                                                                        onClick={() => handleFilterChange('attention')}
                                                                        isActive={activeFilter === 'attention'}
                                                                />
                                                        </div>
                                                </section>

                                                <nav id="navigation" className="w-full mb-8">
                                                        <div className="max-w-[2560px] mx-auto">
                                                                <div className="flex items-center justify-between w-full bg-white/60 p-2 rounded-2xl shadow-card">
                                                                        <div className="flex space-x-2">
                                                                                {/* A single site never gets tabs, but the name still
                                                                                    belongs here so the nav says where you are. */}
                                                                                {!isMultiSite && activeSite && (
                                                                                        <span className="px-6 py-3 font-medium text-text-strong">
                                                                                                <span data-testid="site-name-static">{activeSite.name}</span>
                                                                                                <ScoreBadge score={activeSite.score} testId="site-score" className="ml-3" />
                                                                                        </span>
                                                                                )}
                                                                                {isMultiSite && (
                                                                                        <>
                                                                                                <button
                                                                                                        onClick={() => handleSiteChange(null)}
                                                                                                        data-testid="site-tab-all"
                                                                                                        className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${!selectedSite ? 'bg-brand/5 text-text-strong shadow-lg' : 'text-text-weak hover:text-text-strong hover:bg-white/50'}`}>
                                                                                                        All Sites
                                                                                                        <ScoreBadge score={customer.score} testId="customer-score" className="ml-3" />
                                                                                                </button>
                                                                                                {customer.sites.map(site => (
                                                                                                        <button
                                                                                                                key={site.id}
                                                                                                                onClick={() => handleSiteChange(site)}
                                                                                                                data-testid="site-tab"
                                                                                                                data-site-id={site.id}
                                                                                                                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${selectedSite && selectedSite.id === site.id ? 'bg-brand/5 text-text-strong shadow-lg' : 'text-text-weak hover:text-text-strong hover:bg-white/50'}`}>
                                                                                                                {site.name}
                                                                                                                <ScoreBadge score={site.score} testId="site-score" className="ml-3" />
                                                                                                        </button>
                                                                                                ))}
                                                                                        </>
                                                                                )}
                                                                        </div>
                                                                        <div className="flex items-center space-x-2">
                                                                                {isMultiSite && (
                                                                                        <>
                                                                                                {/* Adding a location lives in settings, at every site
                                                                                                    count, so it has one home rather than appearing here
                                                                                                    only once a customer is already multi-site. */}
                                                                                                {selectedSite && (
                                                                                                        <>
                                                                                                                <button
                                                                                                                        onClick={() => siteRenameModal.open(selectedSite)}
                                                                                                        data-testid="site-rename"
                                                                                                                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                                                        title="Rename site"
                                                                                                                >
                                                                                                                        <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                                                                                                                </button>
                                                                                                                <button
                                                                                                                        onClick={() => siteDeleteModal.open(selectedSite)}
                                                                                                        data-testid="site-delete"
                                                                                                                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                                                                                                        title="Delete site"
                                                                                                                >
                                                                                                                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                                                                                                </button>
                                                                                                        </>
                                                                                                )}
                                                                                                <div className="w-8" />
                                                                                        </>
                                                                                )}
                                                                                {/* With a site active the room goes there; under "All Sites"
                                                                                    AddRoom asks which site instead, so the button never
                                                                                    disappears just because no site is selected. */}
                                                                                <AddRoom
                                                                                        siteId={activeSite?.id}
                                                                                        sites={customer.sites}
                                                                                        onRoomAdded={handleRoomAdded}
                                                                                />

                                                                                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                                                                                        <button
                                                                                                onClick={() => handleViewModeChange('cards')}
                                                                                                data-testid="view-cards"
                                                                                                className={`p-2 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-brand' : 'text-gray-400 hover:text-gray-600'}`}
                                                                                                title="Card view"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faGrip} className="w-4 h-4" />
                                                                                        </button>
                                                                                        <button
                                                                                                onClick={() => handleViewModeChange('list')}
                                                                                                data-testid="view-list"
                                                                                                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-brand' : 'text-gray-400 hover:text-gray-600'}`}
                                                                                                title="List view"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faList} className="w-4 h-4" />
                                                                                        </button>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </nav>

                                                {roomsToShow && roomsToShow.length > 0 ? (
                                                        roomsToShow.map(room => (
                                                                <RoomSection
                                                                        key={room.id}
                                                                        room={room}
                                                                        viewMode={viewMode}
                                                                        onMoveDevice={moveModal.open}
                                                                        onDeviceUpdate={refresh}
                                                                        onRenameDevice={renameModal.open}
                                                                        onRenameRoom={roomRenameModal.open}
                                                                        onDeleteRoom={roomDeleteModal.open}
                                                                />
                                                        ))
                                                ) : (
                                                        <div className="text-center py-12">
                                                                <p data-testid="dashboard-empty-rooms" className="text-lg text-gray-600">
                                                                        {activeFilter === 'attention'
                                                                                ? 'No devices need attention right now.'
                                                                                : activeFilter === 'thriving'
                                                                                        ? 'No thriving plants found.'
                                                                                        : activeSite
                                                                                                ? 'This site has no rooms.'
                                                                                                : 'You have no rooms yet. Add one to get started.'}
                                                                </p>
                                                        </div>
                                                )}
                                        </>
                                ) : (
                                        <div className="text-center py-12">
                                                <h2 data-testid="dashboard-welcome" className="text-2xl font-bold text-gray-800 mb-4">Welcome to CleverPlant!</h2>
                                                <p className="text-lg text-gray-600">It looks like you don't have any sites or rooms set up yet.</p>
                                                <p className="text-lg text-gray-600 mt-2">Get started by creating a new site and adding rooms to it.</p>
                                                <div className="mt-8">
                                                        <AddSite onSiteAdded={handleSiteAdded} initialSetup={true} customerId={targetCustomerId} />
                                                </div>
                                        </div>
                                )}
                        </main>

                        <Footer />

                        <MoveDeviceModal
                                isOpen={moveModal.isOpen}
                                onClose={moveModal.close}
                                device={moveModal.item}
                                sites={customer ? customer.sites : []}
                                onMove={handleMoveDevice}
                        />

                        <RenameDeviceModal
                                isOpen={renameModal.isOpen}
                                onClose={renameModal.close}
                                device={renameModal.item}
                                onRename={handleRenameDevice}
                        />

                        <RenameModal
                                isOpen={siteRenameModal.isOpen}
                                onClose={siteRenameModal.close}
                                item={siteRenameModal.item}
                                itemType="Site"
                                onRename={handleRenameSite}
                        />

                        <ConfirmationModal
                                isOpen={siteDeleteModal.isOpen}
                                onClose={siteDeleteModal.close}
                                onConfirm={handleDeleteSite}
                                title="Delete Site"
                                message={`Are you sure you want to delete the site ${siteDeleteModal.item?.name}?`}
                        />

                        <RenameModal
                                isOpen={roomRenameModal.isOpen}
                                onClose={roomRenameModal.close}
                                item={roomRenameModal.item}
                                itemType="Room"
                                onRename={handleRenameRoom}
                        />

                        <ConfirmationModal
                                isOpen={roomDeleteModal.isOpen}
                                onClose={roomDeleteModal.close}
                                onConfirm={handleDeleteRoom}
                                title="Delete Room"
                                message={`Are you sure you want to delete the room ${roomDeleteModal.item?.name}?`}
                        />
                </div>
        );
};

export default DashboardPage;
